import {ACCEPTED_IMAGE_FILE_TYPES, MAX_TASK_IMAGES, useMarkdownImageUpload} from '../use-markdown-image-upload'
import {useClickFileSelect} from '@github-ui/use-unified-file-select'
import {ImageIcon} from '@primer/octicons-react'
import {IconButton, type IconButtonProps, Tooltip} from '@primer/react'
import type {RefObject} from 'react'

import styles from './ImageAttachButton.module.css'
import {useComposerTelemetry} from '../../telemetry/ComposerTelemetryContext'

export type ImageAttachButtonProps = {
  /** Ref to the textarea where uploaded image markdown will be inserted. */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** Repository ID used for asset uploads. When undefined, the button is disabled. */
  repositoryId?: string
  /** Whether the button should be disabled (e.g. no repo selected). */
  disabled?: boolean
  /** Whether the image limit has been reached. When true, the button is disabled with a tooltip. */
  isAtImageLimit?: boolean
  /** Direction for the button tooltip (e.g. 'n' when rendered at the bottom of a side panel) */
  tooltipDirection?: IconButtonProps['tooltipDirection']
}

/**
 * Self-contained image attach button that opens a native file picker on click
 * and uploads selected images, inserting markdown into the referenced textarea.
 */
export function ImageAttachButton({
  textAreaRef,
  repositoryId,
  disabled,
  isAtImageLimit,
  tooltipDirection,
}: ImageAttachButtonProps) {
  const {processFiles, isUploading} = useMarkdownImageUpload({
    textAreaRef,
    repositoryId,
  })

  const telemetry = useComposerTelemetry()
  const clickTargetProps = useClickFileSelect({
    onSelect: (accepted: File[]) => {
      telemetry?.noteAttachmentVia('image_button')
      return processFiles(accepted)
    },
    multi: true,
    acceptedFileTypes: ACCEPTED_IMAGE_FILE_TYPES,
  })

  const isInactive = disabled || isUploading || isAtImageLimit

  const button = (
    <IconButton
      icon={ImageIcon}
      aria-label={isAtImageLimit ? `Image limit reached (${MAX_TASK_IMAGES})` : 'Attach an image'}
      variant="invisible"
      className={styles.attachButton}
      tooltipDirection={tooltipDirection}
      inactive={isInactive}
      aria-disabled={isInactive}
      {...(!isInactive ? clickTargetProps : {})}
    />
  )

  if (isAtImageLimit) {
    return (
      <Tooltip text={`You can attach up to ${MAX_TASK_IMAGES} images per task`} direction={tooltipDirection ?? 'n'}>
        {button}
      </Tooltip>
    )
  }

  return button
}
